<script setup lang="ts">
import type { InsightConfigBrowserFilters } from '../../../composables/useInsightConfigBrowser'
import type { InsightHistoryFilters } from '../../../composables/useInsightHistory'
import { useInsightWorkbench } from '../../../composables/useInsightWorkbench'
import type { InsightExecutionForm } from '../../../models/workbench.types'
import InsightSummaryCards from '../../display/InsightSummaryCards.vue'

import InsightConfigBrowser from './InsightConfigBrowser.vue'
import InsightConfigInspector from './InsightConfigInspector.vue'
import InsightExecuteModal from './InsightExecuteModal.vue'
import InsightHistoryPanel from './InsightHistoryPanel.vue'
import InsightPreviewPanel from './InsightPreviewPanel.vue'
import InsightWorkbenchTabs from './InsightWorkbenchTabs.vue'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { useToast } from '@/shared/ui/feedback'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'

const { t } = useI18n()
const toast = useToast()
const workbench = useInsightWorkbench()
const {
  hasContext,
  canUseWorkbench,
  state,
  activeTab,
  selectedConfigId,
  selectedConfig,
  summaryCards,
  browser,
  history,
  execute,
  preview,
} = workbench

function updateConfigFilters(value: InsightConfigBrowserFilters): void {
  browser.draftFilters.value = value
}
function updateHistoryFilters(value: InsightHistoryFilters): void {
  history.draftFilters.value = value
}
function updateForm(value: InsightExecutionForm): void {
  Object.assign(execute.form, value)
}
async function persist(): Promise<void> {
  const outcome = await workbench.persist()
  if (outcome.kind === 'created') toast.success(t('insights.sysadmin.execute.created'))
}
</script>

<template>
  <section class="insight-workbench grid gap-lg min-w-0" data-testid="sysadmin-insight-workbench">
    <PageHeader
      :title="t('insights.sysadmin.workbench.title')"
      :subtitle="t('insights.sysadmin.workbench.subtitle')"
    >
      <template #actions>
        <Button variant="ghost" :disabled="!hasContext" @click="workbench.refreshAll">
          {{ t('insights.sysadmin.workbench.refreshAll') }}
        </Button>
        <Button :disabled="!hasContext || !canUseWorkbench" @click="execute.show">
          {{ t('insights.sysadmin.execute.open') }}
        </Button>
      </template>
    </PageHeader>

    <Alert v-if="!hasContext" tone="warning" data-testid="insight-workbench-no-context">
      {{ t('insights.sysadmin.workbench.noWorkspace') }}
    </Alert>
    <Alert v-else-if="!canUseWorkbench" tone="danger">
      {{ t('insights.sysadmin.workbench.forbidden') }}
    </Alert>
    <div
      v-else-if="state === 'loading'"
      class="insight-workbench__loading grid gap-lg min-w-0"
      aria-busy="true"
    >
      <Skeleton height="7rem" />
      <Skeleton height="28rem" />
    </div>
    <Alert v-else-if="state === 'error'" tone="danger">
      {{ t('insights.sysadmin.workbench.error') }}
      <Button variant="ghost" size="sm" @click="workbench.refreshAll">
        {{ t('insights.error.retry') }}
      </Button>
    </Alert>
    <template v-else>
      <InsightSummaryCards :cards="summaryCards" />
      <div
        class="insight-workbench__layout grid grid-cols-[minmax(17rem,_22rem)_minmax(0,_1fr)] gap-lg items-start max-lg:grid-cols-[minmax(0,_1fr)]"
      >
        <InsightConfigBrowser
          :configs="browser.configs.value"
          :selected-id="selectedConfigId"
          :filters="browser.draftFilters.value"
          :state="browser.state.value"
          :total="browser.total.value"
          :offset="browser.offset.value"
          :page-size="20"
          @update:selected-id="workbench.selectConfig"
          @update:filters="updateConfigFilters"
          @apply="browser.applyFilters"
          @clear="browser.clearFilters"
          @retry="browser.load"
          @next="browser.nextPage"
          @previous="browser.previousPage"
        />

        <div
          class="insight-workbench__canvas min-w-0 p-md border border-border rounded-lg bg-surface"
        >
          <InsightWorkbenchTabs v-model="activeTab">
            <template #preview>
              <InsightPreviewPanel :config="selectedConfig" :state="preview.state.value" />
            </template>
            <template #config><InsightConfigInspector :config="selectedConfig" /></template>
            <template #history>
              <InsightHistoryPanel
                :groups="history.grouped.value"
                :group-by="history.groupBy.value"
                :selected="history.selected.value"
                :filters="history.draftFilters.value"
                :state="history.state.value"
                :detail-state="history.detailState.value"
                :detail-tab="history.detailTab.value"
                @update:filters="updateHistoryFilters"
                @update:group-by="history.groupBy.value = $event"
                @update:detail-tab="history.detailTab.value = $event"
                @apply="history.applyFilters"
                @clear="history.clearFilters"
                @retry="history.load"
                @select="history.selectResult"
              />
            </template>
          </InsightWorkbenchTabs>
        </div>
      </div>
    </template>

    <InsightExecuteModal
      :open="execute.open.value"
      :form="execute.form"
      :configs="browser.configs.value"
      :can-persist="preview.canPersist.value"
      :loading="preview.state.value.kind === 'loading'"
      :validation-key="execute.validationKey.value"
      @update:open="execute.setOpen"
      @update:form="updateForm"
      @dry-run="workbench.runDryRun"
      @persist="persist"
    />
  </section>
</template>
